import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { sprintf } from 'sprintf-js';
import _ from 'lodash';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import { FILTERS_NAME_KEY } from 'common/constants/SiteConstants';

import Icon from 'common/components/styleguide/Icon';
import { getLanguageSelector, getActiveRouteSelector } from 'common/selectors';
import { getPageDataSelector } from 'common/selectors/page';
import {
  removeAllActiveFilters,
  removePageActiveFilter
} from 'common/actions/PageActions';

import { capfl, hashCode } from 'common/utils';

const DEFAULT_DANGER_COLOR = '#dc3545';
const DEFAULT_BORDER = '1px solid #eee';

const DEFAULT_REMOVE_COLOR = '#222';
const DEFAULT_REMOVE_BACKGROUND = '#efefef';

const DEFAULT_REMOVE_HOVER_COLOR = '#fff';
const DEFAULT_REMOVE_HOVER_BACKGROUND = DEFAULT_DANGER_COLOR;

const S = {};

S.Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

S.ItemRemove = styled.a`
  display: flex;
  padding: 0 5px;
  height: 28px;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 0.8em;
  color: ${({ theme }) =>
    _.get(
      theme,
      'activeFilter.remove.hover.color',
      DEFAULT_REMOVE_HOVER_COLOR
    )};
  background: ${({ theme }) =>
    _.get(
      theme,
      'activeFilter.remove.hover.background',
      DEFAULT_REMOVE_HOVER_BACKGROUND
    )};
  border: ${({ theme }) => _.get(theme, 'activeFilter.border', DEFAULT_BORDER)};
  margin: ${({ theme }) => _.get(theme, 'activeFilter.margin')};
  &:hover {
    color: ${({ theme }) =>
      _.get(
        theme,
        'activeFilter.remove.hover.color',
        DEFAULT_REMOVE_HOVER_COLOR
      )};
    background: ${({ theme }) =>
      _.get(
        theme,
        'activeFilter.remove.hover.background',
        DEFAULT_REMOVE_HOVER_BACKGROUND
      )};
  }
`;

S.Item = styled.div`
  display: flex;
  height: 28px;
  align-items: center;
  border: ${({ theme }) => _.get(theme, 'activeFilter.border', DEFAULT_BORDER)};
  margin: ${({ theme }) => _.get(theme, 'activeFilter.margin')};
`;

S.Remove = styled.a`
  display: flex;
  height: 26px;
  width: 28px;
  text-decoration: none;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) =>
    _.get(theme, 'activeFilter.remove.color', DEFAULT_REMOVE_COLOR)};
  background: ${({ theme }) =>
    _.get(theme, 'activeFilter.remove.background', DEFAULT_REMOVE_BACKGROUND)};
  .icon {
    font-size: 10px;
  }
  &:hover {
    color: ${({ theme }) =>
      _.get(
        theme,
        'activeFilter.remove.hover.color',
        DEFAULT_REMOVE_HOVER_COLOR
      )};
    background: ${({ theme }) =>
      _.get(
        theme,
        'activeFilter.remove.hover.background',
        DEFAULT_REMOVE_HOVER_BACKGROUND
      )};
  }
`;

S.Name = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 5px;
  font-size: 0.8em;
`;

S.Value = styled.span`
  padding: 0 5px;
  display: flex;
`;

class ActiveFilters extends PureComponent {
  constructor(props) {
    super(props);
    this.activeFiltersItems = [];
  }
  handleMetaRobotsByActiveFilters() {
    const { filters, activeFilters } = this.props;
    const metaExists = document.querySelector('meta[name="robots"]');
    if (metaExists) {
      document.head.removeChild(metaExists);
    }
    const activeFiltersParams =  _.flatten(_.values(_.get(activeFilters, 'param')));
    const activeFiltersWithoutParams = _.flattenDeep(_.values(_.omit(activeFilters, 'param')));
    if (!_.isEmpty(filters) && !_.isEmpty(activeFilters) && _.concat(activeFiltersWithoutParams, activeFiltersParams).length > 2) {
      const metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      metaRobots.content = 'noindex, nofollow';
      document.head.appendChild(metaRobots);
    }
  }
  componentDidMount() {
    this.handleMetaRobotsByActiveFilters();
  }
  componentDidUpdate() {
    this.handleMetaRobotsByActiveFilters();
  }
  getValueLabelFromResolved(activeFilterValue, resolved, filters) {
    const {
      resolvedFilterName = '',
      resolvedLabelKey = '',
      resolvedValueKey = ''
    } = resolved;
    const filter =
      resolvedFilterName && filters[resolvedFilterName]
        ? filters[resolvedFilterName]
        : null;
    let realValue = null;
    if (filter) {
      const founded = _.find(
        filter,
        item =>
          _.toString(item[resolvedValueKey]) === _.toString(activeFilterValue)
      );
      if (founded && founded[resolvedLabelKey]) {
        realValue = founded[resolvedLabelKey];
      }
    }
    return realValue ? realValue : activeFilterValue;
  }
  getSingleFilters(activeFilters, filtersResolve, filters) {
    let activeFiltersSingle = _.cloneDeep(_.omit(activeFilters, 'param'));
    _.each(activeFiltersSingle, (value, name) => {
      const resolved =
        filtersResolve && filtersResolve[name] ? filtersResolve[name] : name;
      if (_.isArray(value)) {
        _.each(value, v => {
          if (_.isObject(resolved)) {
            const { resolvedName = name } = resolved;
            this.activeFiltersItems.push({
              resolvedName,
              name,
              value: this.getValueHumanReadable(name, v),
              valueLabel: this.getValueLabelFromResolved(v, resolved, filters)
            });
          } else {
            this.activeFiltersItems.push({
              resolvedName: resolved,
              name,
              value: this.getValueHumanReadable(name, v)
            });
          }
        });
      } else {
        if (_.isObject(resolved)) {
          const { resolvedName = name } = resolved;
          this.activeFiltersItems.push({
            resolvedName,
            name,
            value: this.getValueHumanReadable(name, value),
            valueLabel: this.getValueLabelFromResolved(value, resolved, filters)
          });
        } else {
          this.activeFiltersItems.push({
            resolvedName: resolved,
            name,
            value: this.getValueHumanReadable(name, value)
          });
        }
      }
    });
  }
  getValueHumanReadable = (activeFilter, value) => {
    const { activeRoute, currentLanguage } = this.props;
    return _.get(activeRoute, ['filterTable', currentLanguage, activeFilter, 'values', value], value);
  }
  static getCustomSettingsByName(filtersTypes, name, value) {
    const type = _.get(filtersTypes, name, '');
    if (type === 'slider') {
      return {
        value: value[0] + '-' + value[1],
        removeWhole: true
      };
    } else {
      return {};
    }
  }
  getParamFilters(activeFilters, filtersResolve, filtersTypes) {
    let activeFiltersParam = activeFilters['param']
      ? _.cloneDeep(activeFilters['param'])
      : null;
    _.each(activeFiltersParam, (value, name) => {
      const resolvedName =
        filtersResolve && filtersResolve[name] ? filtersResolve[name] : name;
      let customSettings = ActiveFilters.getCustomSettingsByName(
        filtersTypes,
        name,
        value
      );
      const removeWhole = !_.isEmpty(customSettings)
        ? customSettings.removeWhole
        : false;
      value = !_.isEmpty(customSettings) ? customSettings.value : value;
      if (_.isArray(value)) {
        _.each(value, v => {
          this.activeFiltersItems.push({
            resolvedName,
            name,
            value: v,
            isParamFilter: true,
            removeWhole: false
          });
        });
      } else {
        this.activeFiltersItems.push({
          resolvedName,
          name,
          value,
          isParamFilter: true,
          removeWhole: removeWhole
        });
      }
    });
  }
  getFiltersTypes(filters) {
    const { parameters } = filters;
    const filtersTypes = [];
    _.each(parameters, item => {
      const filterName = sprintf('%1$s-%2$s', item[FILTERS_NAME_KEY], item.id);
      filtersTypes[filterName] = item.type;
    });
    return filtersTypes;
  }
  onRemoveAllActiveFiltersClick = e => {
    e.preventDefault();
    const { dispatchRemoveAllActiveFilters } = this.props;
    dispatchRemoveAllActiveFilters();
  };
  render() {
    const {
      t,
      filters,
      activeFilters,
      filtersResolve,
      removeActiveFilter
    } = this.props;
    if (!_.isEmpty(filters) && !_.isEmpty(activeFilters)) {
      this.activeFiltersItems = [];
      const filtersTypes = this.getFiltersTypes(filters);
      this.getSingleFilters(activeFilters, filtersResolve, filters);
      this.getParamFilters(activeFilters, filtersResolve, filtersTypes);
      return (
        <S.Wrapper>
          {_.map(this.activeFiltersItems, item => {
            const filterValueId = sprintf(
              '%1$s-%2$s',
              item.name,
              hashCode(item.value)
            );
            return (
              <S.Item key={'active-filter-' + filterValueId}>
                <S.Name>
                  {item.resolvedName} -{' '}
                  <S.Value>
                    {item.valueLabel ? item.valueLabel : item.value}
                  </S.Value>
                </S.Name>
                <S.Remove
                  href="."
                  onClick={removeActiveFilter(
                    item.name,
                    item.value,
                    item.isParamFilter,
                    item.removeWhole
                  )}
                >
                  <Icon className="nc-simple-remove" />
                </S.Remove>
              </S.Item>
            );
          })}
          {this.activeFiltersItems.length >= 2 ? (
            <S.ItemRemove href="." onClick={this.onRemoveAllActiveFiltersClick}>
              {capfl(t('remove all'))}
            </S.ItemRemove>
          ) : (
            ''
          )}
        </S.Wrapper>
      );
    }
    return null;
  }
}

const makeMapStateToProps = () => {
  const getLanguage = getLanguageSelector();
  const getPageData = getPageDataSelector();
  const  getActiveRoute = getActiveRouteSelector();
  return (state, props) => {
    return {
      activeFilters: _.get(getPageData(state, props), 'activeFilters'),
      filters: _.get(getPageData(state, props), 'filter'),
      filtersResolve: _.get(getPageData(state, props), 'filtersResolve'),
      currentLanguage: getLanguage(state, props),
      activeRoute: getActiveRoute(state, props),
    };
  };
};

const mapDispatchToProps = (dispatch, props) => {
  return {
    removeActiveFilter: (
      name,
      value,
      isParamFilter = false,
      removeWhole = false
    ) => e => {
      e.preventDefault();
      const { history } = props;
      dispatch(
        removePageActiveFilter(name, value, isParamFilter, history, removeWhole)
      );
    },
    dispatchRemoveAllActiveFilters: () => {
      const { history } = props;
      dispatch(removeAllActiveFilters(history));
    }
  };
};

const ActiveFiltersConnected = connect(
  makeMapStateToProps,
  mapDispatchToProps
)(ActiveFilters);

export default withTranslation()(withRouter(ActiveFiltersConnected));
